import React, { useState, useEffect, useMemo, useRef } from "react";
import { GoogleMap, useLoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import "./ordersMapStyle.css"

const libraries = ["places"];

const parseQueryParams = (queryString) => {
    const params = new URLSearchParams(queryString);
    return {
        fromLocation: JSON.parse(decodeURIComponent(params.get("fromLocation"))),
        toLocation: JSON.parse(decodeURIComponent(params.get("toLocation"))),
        mode: params.get("mode") || "both",
    };
};

const formatAddress = (address) => {
    return `${address.street}, ${address.city}, ${address.province}, ${address.country}, ${address.zipcode}`;
};

const OrdersMap = () => {
    const { fromLocation, toLocation, mode } = useMemo(
        () => parseQueryParams(window.location.search),
        []
    );
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });

    const [coordinates, setCoordinates] = useState({ from: null, to: null });
    const [activeMarker, setActiveMarker] = useState(null);
    const mapRef = useRef(null);
    const initialBoundsFittedRef = useRef(false);

    const geocodeAddress = async (address) => {
        const geocoder = new window.google.maps.Geocoder();
        const formattedAddress = formatAddress(address);

        return new Promise((resolve, reject) => {
            geocoder.geocode({ address: formattedAddress }, (results, status) => {
                if (status === "OK" && results[0]) {
                    resolve({
                        lat: results[0].geometry.location.lat(),
                        lng: results[0].geometry.location.lng(),
                    });
                } else {
                    reject(`Geocoding error: ${status}`);
                }
            });
        });
    };

    useEffect(() => {
        const loadCoordinates = async () => {
            try {
                const fromCoords = await geocodeAddress(fromLocation);
                const toCoords = await geocodeAddress(toLocation);
                setCoordinates({ from: fromCoords, to: toCoords });
            } catch (error) {
                console.error("Error fetching coordinates:", error);
            }
        };

        if (isLoaded) {
            loadCoordinates();
        }
    }, [isLoaded, fromLocation, toLocation]);

    const markers = useMemo(() => {
        const markerArray = [];
        if (mode === "from" && coordinates.from) {
            markerArray.push({
                position: coordinates.from,
                address: formatAddress(fromLocation),
            });
        }
        if (mode === "to" && coordinates.to) {
            markerArray.push({
                position: coordinates.to,
                address: formatAddress(toLocation),
            });
        }
        if (mode === "both") {
            if (coordinates.from) {
                markerArray.push({
                    position: coordinates.from,
                    address: formatAddress(fromLocation),
                });
            }
            if (coordinates.to) {
                markerArray.push({
                    position: coordinates.to,
                    address: formatAddress(toLocation),
                });
            }
        }
        return markerArray;
    }, [mode, coordinates, fromLocation, toLocation]);

    useEffect(() => {
        const shouldFitBounds = mapRef.current && (coordinates.from || coordinates.to) && !initialBoundsFittedRef.current;
        if (shouldFitBounds) {
            const markerPositions = [];
            if (mode === "both") {
                if (coordinates.from) markerPositions.push(coordinates.from);
                if (coordinates.to) markerPositions.push(coordinates.to);
            } else if (mode === "from" && coordinates.from) {
                markerPositions.push(coordinates.from);
            } else if (mode === "to" && coordinates.to) {
                markerPositions.push(coordinates.to);
            }

            if (markerPositions.length === 1) {
                mapRef.current.setCenter(markerPositions[0]);
                mapRef.current.setZoom(12);
            } else if (markerPositions.length > 1) {
                const bounds = new window.google.maps.LatLngBounds();
                markerPositions.forEach((pos) => bounds.extend(pos));
                mapRef.current.fitBounds(bounds);
            }
            initialBoundsFittedRef.current = true;
        }
    }, [coordinates, mode]);

    if (!isLoaded) {
        return <div>Loading...</div>;
    }

    return (
        <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100vh" }}
            onLoad={(map) => (mapRef.current = map)}
        >
            {markers.map((marker, index) => (
                <React.Fragment key={index}>
                    <Marker
                        position={marker.position}
                        onMouseOver={() => setActiveMarker(index)}
                        onMouseOut={() => setActiveMarker(null)}
                    />
                    {activeMarker === index && (
                        <InfoWindow
                            position={marker.position}
                            onCloseClick={() => setActiveMarker(null)}
                            options={{
                                disableAutoPan: true,
                                pixelOffset: new window.google.maps.Size(0, -40),
                                closeBoxURL: "",
                            }}
                        >
                            <div>{marker.address}</div>
                        </InfoWindow>
                    )}
                </React.Fragment>
            ))}
        </GoogleMap>
    );
};

export default OrdersMap;
